import { Injectable } from '@angular/core';
import { ErrorModel } from '@models/micro/error.model';

/**
 * Criterios de búsqueda soportados por el lookup de reservación.
 * Coincide con el tipo SearchCriterial usado en ApiService.getReservation.
 */
export type FailedLookupCriteria = string;

/**
 * Argumentos que identifican de forma única una búsqueda de reservación.
 * Se usan para construir la clave de cache: dos búsquedas con exactamente
 * los mismos valores se consideran "la misma búsqueda".
 */
export interface FailedLookupKeyArgs {
  criteria: FailedLookupCriteria;
  pnr?: string | null;
  firstName?: string | null;
  lastName?: string | null;
  numberFlight?: string | null;
  arrivalCity?: string | null;
  arrivalAirport?: string | null;
  departureAirport?: string | null;
}

/**
 * Razones de negocio (no fallas técnicas) que devuelve el backend de
 * reservación cuando la búsqueda no encuentra un resultado válido. Estos NO
 * son errores del sistema: el pasajero tecleó mal el PNR, no tiene reserva
 * saliendo de este aeropuerto, etc. Solo estas razones se cachean para evitar
 * relanzar la misma búsqueda condenada al mismo resultado.
 *
 * Un timeout, un 5xx real de Sabre o un error de red NO entran aquí: esos son
 * transitorios y el pasajero debe poder reintentar.
 */
// Tokens EXACTOS validados contra 3 semanas de datos reales de New Relic
// (cuenta 2141807, eventos lookup_pnr_error / lookup_pnr_scanned_error). El
// atributo error_message registrado es justo lo que devuelve getMessage(), y
// los valores reales son:
//   ERR.TC.RESERVATION.NO_RESERVATIONS   (2489)
//   ERR.TC.RESERVATION.PNR_NOT_FOUND     (1533)
//   ERR.TC.RESERVATION.MANY_RESERVATIONS (165)
//   ERR.TC.RESERVATION.NO_ITINERARY      (9)
// Se usa el token especifico PNR_NOT_FOUND (no un generico NOT_FOUND) para no
// cachear por accidente otros *_NOT_FOUND tecnicos. MANY_RESERVATIONS va
// completo para no matchear "TOO MANY REQUESTS" (429 transitorio). NO se
// incluyen 5xx/timeouts/HTML (p.ej. "502 Bad Gateway" que llega como error de
// parseo): son transitorios y deben poder reintentarse.
const BUSINESS_ERROR_MARKERS: readonly string[] = [
  'NO_RESERVATIONS',
  'PNR_NOT_FOUND',
  'MANY_RESERVATIONS',
  'NO_ITINERARY',
];

/**
 * Cachea, durante la vida de la sesión del kiosco, las búsquedas de
 * reservación (PNR) que fallaron por una razón de negocio.
 *
 * Objetivo: cuando un pasajero repite exactamente la misma búsqueda que ya
 * falló (mismo PNR, o mismo destino + apellido), volver a mostrarle el mismo
 * error SIN llamar de nuevo al backend y SIN registrar otro evento isError en
 * New Relic. Esto ataca los reintentos que inflan el error rate (~2.4 eventos
 * de error por sesión en NO_RESERVATIONS).
 *
 * Reglas:
 * - Solo cachea razones de negocio (ver BUSINESS_ERROR_MARKERS).
 * - Si el pasajero cambia el dato (otro PNR / otro destino), la clave cambia y
 *   la nueva búsqueda sí llama al backend.
 * - El cache vive solo en memoria durante la sesión. Al reiniciar la sesión
 *   del kiosco (GlobalsService) se limpia con clear(), de modo que la primera
 *   búsqueda de la siguiente sesión siempre llama al backend.
 */
@Injectable({
  providedIn: 'root',
})
export class FailedPnrLookupCacheService {
  private readonly failedLookups = new Map<string, ErrorModel>();

  /**
   * Construye una clave estable e insensible a mayúsculas/espacios a partir de
   * los argumentos de búsqueda. Búsquedas equivalentes producen la misma clave.
   */
  buildKey(args: FailedLookupKeyArgs): string {
    const normalize = (value?: string | null): string => (value ?? '').trim().toUpperCase();

    return [
      normalize(args.criteria),
      normalize(args.pnr),
      normalize(args.firstName),
      normalize(args.lastName),
      normalize(args.numberFlight),
      normalize(args.arrivalCity),
      normalize(args.arrivalAirport),
      normalize(args.departureAirport),
    ].join('|');
  }

  /**
   * Determina si un error corresponde a una razón de negocio cacheable.
   * Inspecciona tanto el mensaje del ErrorModel como el reason del raw.
   */
  isBusinessError(error: ErrorModel | undefined | null): boolean {
    if (!error) {
      return false;
    }
    const message = (error.getMessage?.() ?? '').toUpperCase();
    const rawReason = (error.getRaw?.()?.reason ?? '').toString().toUpperCase();
    return BUSINESS_ERROR_MARKERS.some(
      (marker) => message.includes(marker) || rawReason.includes(marker)
    );
  }

  /**
   * Recupera el error cacheado para una búsqueda, o undefined si esa búsqueda
   * no ha fallado antes en esta sesión.
   */
  get(args: FailedLookupKeyArgs): ErrorModel | undefined {
    return this.failedLookups.get(this.buildKey(args));
  }

  /**
   * Guarda el error de una búsqueda fallida, únicamente si es una razón de
   * negocio. Los errores técnicos/transitorios no se cachean para permitir
   * reintentos legítimos.
   */
  set(args: FailedLookupKeyArgs, error: ErrorModel): void {
    if (!this.isBusinessError(error)) {
      return;
    }
    this.failedLookups.set(this.buildKey(args), error);
  }

  /**
   * Limpia todas las búsquedas fallidas cacheadas. Debe invocarse al reiniciar
   * la sesión del kiosco para que la siguiente sesión parta de cero.
   */
  clear(): void {
    this.failedLookups.clear();
  }
}
